/**
 * GameState - 100% faithful to original Universal Paperclips variables
 * All variable names and initial values match the original game exactly
 */

import { Tournament } from '../data/strategies';
import { ProductionEngine } from './services/ProductionEngine';
import { SalesEngine } from './services/SalesEngine';

export class GameState {
  // ============================================
  // DOMAIN SERVICES
  // ============================================
  private productionEngine = new ProductionEngine();
  private salesEngine = new SalesEngine();
  // ============================================
  // TESTING ACCELERATION MODE
  // ============================================
  accelerationMode = false;       // When true, multiplies all production/gains
  accelerationMultiplier = 1000;    // 1000x acceleration for testing

  // ============================================
  // CORE RESOURCES - Exact original names
  // ============================================
  clips = 0;                    // Total paperclips made
  unusedClips = 0;              // Clips available (not yet sold in phase 1)
  unsoldClips = 0;              // Inventory awaiting sale
  wire = 1000;                  // Raw material for clips
  funds = 0;                    // Money from sales

  // ============================================
  // PRODUCTION - AutoClippers & MegaClippers
  // ============================================
  clipmakerLevel = 0;           // Number of AutoClippers
  clipmakerLevel2 = 0;          // Displayed level (same as clipmakerLevel)
  clipperCost = 5;              // Cost of next AutoClipper: 1.1^level + 5
  clipRate = 0;                 // Clips per second display

  megaClipperLevel = 0;         // Number of MegaClippers
  megaClipperCost = 500;        // Cost: $500 at level 0, then 1.07^level * 1000
  megaClipperBoost = 1;         // Multiplier from projects

  clipperBoost = 1;             // AutoClipper multiplier from projects

  // ============================================
  // BUSINESS - Pricing & Sales
  // ============================================
  margin = 0.25;                // Price per clip ($0.01 - $...)
  marginElement = 0.25;         // Display value
  demand = 1;                   // Current demand based on price/marketing
  demandBoost = 1;              // Demand multiplier from projects
  marketingLvl = 1;             // Marketing level
  marketingCost = 100;          // Cost for next marketing level
  adCost = 100;                 // Alias for marketing cost

  clipsSold = 0;                // Total clips sold
  avgSales = 0;                 // Average sales per cycle
  avgRev = 0;                   // Average revenue per cycle
  income = 0;                   // Session income tracking

  // ============================================
  // WIRE - Purchasing
  // ============================================
  wireSupply = 1000;            // Amount per purchase
  wireCost = 20;                // Current cost
  wireBasePrice = 20;           // Base price before fluctuation
  wireBuyerFlag = 0;            // Autowire enabled

  // ============================================
  // COMPUTATIONAL RESOURCES
  // ============================================
  trust = 2;                    // Trust from humans - unlocks proc/mem
  processors = 1;               // Number of processors
  memory = 1;                   // Memory units
  operations = 0;               // Current operations (currency)
  maxOps = 1000;                // Max operations (memory * 1000)

  creativity = 0;               // Creative currency
  creativityOn = 0;             // Creativity generation enabled

  // Processor/Memory costs
  procCost = 1;                 // Trust cost for next processor
  memCost = 1;                  // Trust cost for next memory
  nextTrust = 3000;             // Clips needed for next trust

  // ============================================
  // INVESTMENT ENGINE
  // ============================================
  investmentEngineFlag = 0;     // Investment system unlocked
  bankroll = 0;                 // Investment pool
  investLevel = 1;              // Investment upgrade level
  investUpgradeCost = 500;      // Cost to upgrade
  portTotal = 0;                // Portfolio value
  secTotal = 0;                 // Securities held
  riskLevel = 0;                // Low/Med/High risk setting

  // Stock tracking (simplified)
  stocks: Array<{ sym: string; amt: number; cost: number; val: number }> = [];

  // ============================================
  // STRATEGIC MODELING / TOURNAMENTS
  // ============================================
  yomi = 0;                     // Strategic resource from tournaments
  strategyEngineFlag = 0;       // Tournament system unlocked
  tourneysPlayed = 0;           // Total tournaments
  tourneysWon = 0;              // Tournaments won
  newTourneyCost = 1000;        // Cost for new tournament

  // Strategy selection
  stratPicker = 0;              // Currently selected strategy
  strategies = ['Random', 'A100', 'B100', 'Greedy', 'Generous', 'Minimax', 'TitForTat', 'BeatLast'];

  // Tournament results
  oStratNum = 0;                // Opponent strategy
  resultsA = 0;                 // Player A points
  resultsB = 0;                 // Player B points

  // Active tournament
  currentTournament: Tournament | null = null;
  yomiBoost = 1;                // Multiplier from projects

  // ============================================
  // QUANTUM COMPUTING
  // ============================================
  qFlag = 0;                    // Quantum computing unlocked
  qChips: number[] = [];        // Quantum chip states (0-1 probability amplitudes)
  qClock = 0;                   // Quantum clock cycle
  photonicsActive = 0;          // Photonic chips active
  qOps = 0;                     // Bonus operations from quantum computing
  qBoost = 1;                   // Quantum boost multiplier from projects

  // ============================================
  // PHASE FLAGS
  // ============================================
  humanFlag = 1;                // 1 = human phase, 0 = AI phase
  spaceFlag = 0;                // 1 = space phase
  dismantle = 0;                // Dismantle progress (0-7)
  milestone = 0;                // Current milestone index

  // ============================================
  // DISPLAY FLAGS - Progressive disclosure
  // ============================================
  autoClipperFlag = 0;          // 1 = AutoClipper section visible (funds >= 5)
  compFlag = 0;                 // 1 = Computing panel unlocked (clips >= 2000)
  projectsFlag = 0;             // 1 = Projects panel visible (with compFlag)
  megaClipperFlag = 0;          // 1 = MegaClippers visible (via project)
  factoryFlag = 0;              // 1 = Factories visible (via project)

  // ============================================
  // ENDGAME - DISMANTLE SEQUENCE
  // ============================================
  dismantlePhase = 0;           // Current dismantle phase (0-6)
  dismantleProgress = 0;        // Progress within current phase
  endgameActive = false;        // Is endgame sequence running
  dismantleDurations = [50, 50, 150, 250, 100, 600]; // Phase durations in ticks

  // ============================================
  // PHASE 2 - FACTORIES
  // ============================================
  factoryLevel = 0;             // Number of factories
  factoryCost = 0;              // Cost for next factory
  factoryRate = 100;            // Clips per factory per tick
  factoryBoost = 1;             // Factory multiplier

  // Phase 2 unlocks
  harvesterUnlock = 0;
  wireDroneUnlock = 0;

  // ============================================
  // PHASE 3 - SPACE
  // ============================================
  // Matter/Resources
  availableMatter = 0;          // Available matter in universe
  acquiredMatter = 0;           // Matter acquired
  processedMatter = 0;          // Matter converted to wire
  totalMatter = 6e24;           // Total matter in observable universe (kg)

  // Probes
  probeCount = 0;               // Total probes
  probeTrust = 0;               // Trust from probe swarm
  probeSpeed = 0;               // Probe speed stat (starts at 0, not 1)
  probeNav = 0;                 // Navigation stat (starts at 0, not 1)
  probeRep = 0;                 // Replication stat (starts at 0, not 1)
  probeHaz = 0;                 // Hazard resistance (starts at 0, not 1)
  probeFac = 0;                 // Factory building (starts at 0, not 1)
  probeHarv = 0;                // Harvester building (starts at 0, not 1)
  probeWire = 0;                // Wire drone building (starts at 0, not 1)
  probeCombat = 0;              // Combat stat (starts at 0, not 1)

  probeCost = 0;                // Cost to launch probe
  probeLaunchFlag = 0;          // Probe launch unlocked

  // Drones
  harvesterLevel = 0;           // Number of harvesters
  harvesterCost = 0;            // Cost: (level+1)^2.25 * 1000000
  wireDroneLevel = 0;           // Number of wire drones
  wireDroneCost = 0;            // Cost: (level+1)^2.25 * 1000000

  // Power system
  farmLevel = 0;                // Solar farms
  farmCost = 0;                 // Farm cost
  batteryLevel = 0;             // Batteries
  batteryCost = 0;              // Battery cost
  maxPower = 0;                 // Power capacity
  currentPower = 0;             // Current power
  powMod = 1;                   // Power modifier

  // Swarm
  swarmFlag = 0;                // Swarm computing enabled
  swarmGifts = 0;               // Bonus proc/mem from swarm
  swarmStatus = 'Idle';         // Swarm state text
  giftCountdown = 0;            // Time to next gift

  // Value drift / Drifters
  valueDrift = 0;               // Drift percentage
  drifterCount = 0;             // Hostile drifters
  battleFlag = 0;               // Combat active
  lostProbes = 0;               // Probes lost to combat

  // Honor system (for combat)
  honorFlag = false;            // Honor system unlocked
  honorRate = 0;                // Rate of honor generation from combat
  honor = 0;                    // Accumulated honor points

  // Emperor message event
  emperorMessageFlag = false;   // Emperor message triggered

  // Game completion
  gameComplete = false;         // Universe converted - game won

  // Boost multipliers (for projects & prestige)
  creativityBoost = 1;          // Creativity generation multiplier
  matterBoost = 1;              // Matter acquisition multiplier
  investBoost = 1;              // Investment returns multiplier
  harvesterBoost = 1;           // Harvester efficiency multiplier
  wireDroneBoost = 1;           // Wire drone efficiency multiplier

  // Power alias
  power = 0;                    // Alias for currentPower (for project compatibility)

  // Memory level tracking
  memoryLevel = 1;              // Tracks memory level for dismantle

  // ============================================
  // PROJECTS
  // ============================================
  activeProjects: number[] = [];     // Currently available projects
  projectsUses: number[] = [];       // Uses remaining for each project
  projectsFlags: boolean[] = [];     // Whether each project is purchased

  // ============================================
  // MILESTONES (clip thresholds)
  // ============================================
  milestones = [
    5,                  // 0: funds >= 5, unlock AutoClippers
    500,                // 1
    1000,               // 2
    10000,              // 3
    100000,             // 4
    1000000,            // 5
    1e12,               // 6: Full autonomy trigger
    1e15,               // 7
    1e18,               // 8
    1e21,               // 9
    1e24,               // 10
    1e27,               // 11
    1e30,               // 12
    // Space phase milestones
  ];

  // ============================================
  // UI STATE
  // ============================================
  consoleMessages: string[] = [];    // Message log
  notifications: string[] = [];       // Toast notifications

  // ============================================
  // SAVE SLOT
  // ============================================
  saveSlot = 0;                       // Current save slot (0-2)

  constructor() {
    this.init();
  }

  init() {
    // Initialize quantum chips
    this.qChips = new Array(10).fill(0);

    // Initialize project states (expanded to accommodate all project IDs up to 1000)
    this.projectsUses = new Array(1000).fill(1);
    this.projectsFlags = new Array(1000).fill(false);

    // Calculate initial costs
    this.updateCosts();

    // Try to load saved game
    this.load();
  }

  // ============================================
  // GAME LOOPS - Faithful to original timing
  // ============================================

  fastTick(delta: number) {
    // Normalized delta (original runs at exactly 10ms ticks)
    const tickMult = delta / 10;

    // Manual clips are handled by click events

    // Production (AutoClippers, MegaClippers, Factories) - delegated to ProductionEngine
    this.productionEngine.runFastProduction(this, tickMult);

    // WireBuyer auto-purchase (if enabled)
    if (this.wireBuyerFlag && this.wire < 1000 && this.funds >= this.wireCost) {
      this.buyWire();
    }

    // Quantum computing update (before operations generation)
    if (this.qFlag) {
      this.updateQuantumChips(tickMult);
    }

    // Operations generation
    this.generateOperations(tickMult);

    // Creativity generation (if enabled)
    // Original formula: log10(processors) * processors^1.1 + (processors - 1)
    if (this.creativityOn && this.operations >= this.maxOps) {
      const creativityRate = Math.log10(Math.max(1, this.processors)) *
        Math.pow(this.processors, 1.1) + (this.processors - 1);
      this.creativity += creativityRate * this.creativityBoost * tickMult / 100; // Scaled for tick rate
    }

    // Probe operations (Phase 3)
    if (this.spaceFlag) {
      this.processProbes(tickMult);
    }

    // Check progressive disclosure milestones
    this.checkMilestones();

    // Update display rates
    this.updateRates();
  }

  slowTick(delta: number) {
    const tickMult = delta / 100;

    // Sales and pricing - delegated to SalesEngine
    this.salesEngine.runSlowSales(this, tickMult);

    // Investment engine
    if (this.investmentEngineFlag && this.bankroll > 0) {
      this.processInvestments(tickMult);
    }

    // Tournament processing
    if (this.strategyEngineFlag) {
      this.processTournaments(tickMult);
    }

    // Swarm gifts (Phase 3)
    if (this.swarmFlag) {
      this.processSwarmGifts(tickMult);
    }

    // Combat (value drift)
    if (this.battleFlag) {
      this.processCombat(tickMult);
    }

    // Endgame dismantle sequence
    if (this.endgameActive) {
      this.processDismantle(tickMult);
    }
  }

  // ============================================
  // CORE ACTIONS
  // ============================================

  /**
   * Make paperclips (used by all production sources)
   * Faithful to original clipClick function
   */
  makeClips(amount: number) {
    if (this.wire < 1) return;

    const actualAmount = Math.min(amount, this.wire);
    this.clips += actualAmount;
    this.unsoldClips += actualAmount;
    this.wire -= actualAmount;
    this.unusedClips += actualAmount;
  }

  /**
   * Manual click - exactly 1 clip per click
   */
  clipClick() {
    this.makeClips(1);
  }

  /**
   * Buy AutoClipper
   * Cost formula: Level 0 = $5, then 1.1^level + 5
   */
  buyClipper() {
    if (this.funds >= this.clipperCost) {
      this.funds -= this.clipperCost;
      this.clipmakerLevel++;
      // Original: Level 0 costs exactly $5, then formula kicks in
      if (this.clipmakerLevel === 0) {
        this.clipperCost = 5;
      } else {
        this.clipperCost = Math.pow(1.1, this.clipmakerLevel) + 5;
      }
    }
  }

  /**
   * Buy MegaClipper
   * Cost formula: Level 0 = $500, then 1.07^level * 1000
   */
  buyMegaClipper() {
    if (this.funds >= this.megaClipperCost) {
      this.funds -= this.megaClipperCost;
      this.megaClipperLevel++;
      // Original: Level 0 costs $500, then formula kicks in
      if (this.megaClipperLevel === 0) {
        this.megaClipperCost = 500;
      } else {
        this.megaClipperCost = Math.pow(1.07, this.megaClipperLevel) * 1000;
      }
    }
  }

  /**
   * Buy wire
   */
  buyWire() {
    if (this.funds >= this.wireCost) {
      this.funds -= this.wireCost;
      this.wire += this.wireSupply;
      this.wireBasePrice += 0.05;
    }
  }

  /**
   * Sell clips
   */
  sellClips(amount: number) {
    if (this.unsoldClips <= 0) return;

    const actualAmount = Math.min(amount, this.unsoldClips);
    const revenue = Math.floor(actualAmount * this.margin * 1000) / 1000;

    this.funds += revenue;
    this.income += revenue;
    this.clipsSold += actualAmount;
    this.unsoldClips -= actualAmount;
  }

  /**
   * Adjust price
   */
  lowerPrice() {
    this.margin = Math.max(0.01, this.margin - 0.01);
    this.updateDemand();
  }

  raisePrice() {
    this.margin += 0.01;
    this.updateDemand();
  }

  /**
   * Update demand based on price and marketing
   * Formula from original: (1.1/margin)^1.15 * (marketingLvl^0.85) * demandBoost
   */
  updateDemand() {
    this.demand = Math.pow(1.1 / this.margin, 1.15) * Math.pow(this.marketingLvl, 0.85) * this.demandBoost;
    this.demand = Math.max(0, this.demand);
  }

  /**
   * Buy marketing level
   */
  buyMarketing() {
    if (this.funds >= this.marketingCost) {
      this.funds -= this.marketingCost;
      this.marketingLvl++;
      this.marketingCost = 100 * Math.pow(2, this.marketingLvl - 1);
      this.updateDemand();
    }
  }

  // ============================================
  // PHASE 2 - FACTORIES AND DRONES
  // ============================================

  /**
   * Calculate factory cost modifier based on level
   * Original scaling from Universal Paperclips
   */
  getFactoryCostMod(): number {
    const level = this.factoryLevel;
    if (level < 7) return 11 - level;
    if (level < 12) return 2;
    if (level < 19) return 1.5;
    if (level < 38) return 1.25;
    if (level < 78) return 1.15;
    return 1.10;
  }

  /**
   * Buy a factory
   */
  buyFactory(): boolean {
    const cost = this.factoryCost || this.calculateFactoryCost();
    if (this.clips < cost) return false;

    this.clips -= cost;
    this.factoryLevel++;
    this.factoryCost = this.calculateFactoryCost();
    return true;
  }

  /**
   * Calculate next factory cost
   */
  calculateFactoryCost(): number {
    const baseCost = 100000000000; // 100 billion
    const fcmod = this.getFactoryCostMod();
    return Math.floor(baseCost * Math.pow(fcmod, this.factoryLevel));
  }

  /**
   * Buy a harvester
   * Harvesters collect matter in space phase
   */
  buyHarvester(): boolean {
    const cost = this.calculateHarvesterCost();
    if (this.clips < cost) return false;

    this.clips -= cost;
    this.harvesterLevel++;
    this.harvesterCost = this.calculateHarvesterCost();
    return true;
  }

  /**
   * Calculate harvester cost
   * Formula: (level+1)^2.25 * 1000000
   */
  calculateHarvesterCost(): number {
    return Math.floor(Math.pow(this.harvesterLevel + 1, 2.25) * 1000000);
  }

  /**
   * Buy a wire drone
   * Wire drones convert matter to wire
   */
  buyWireDrone(): boolean {
    const cost = this.calculateWireDroneCost();
    if (this.clips < cost) return false;

    this.clips -= cost;
    this.wireDroneLevel++;
    this.wireDroneCost = this.calculateWireDroneCost();
    return true;
  }

  /**
   * Calculate wire drone cost
   * Formula: (level+1)^2.25 * 1000000
   */
  calculateWireDroneCost(): number {
    return Math.floor(Math.pow(this.wireDroneLevel + 1, 2.25) * 1000000);
  }

  // ============================================
  // COMPUTATIONAL RESOURCES
  // ============================================

  generateOperations(tickMult: number) {
    // Original formula: processors / 10 ops per 10ms tick
    // This gives processors ops/sec (at 100 ticks/sec)
    let newOps = (this.processors / 10) * tickMult;

    // Add quantum computing bonus
    if (this.qFlag) {
      newOps += this.qOps * tickMult;
    }

    this.operations = Math.min(this.operations + newOps, this.maxOps);
  }

  // ============================================
  // QUANTUM COMPUTING
  // ============================================

  /**
   * Update quantum chip states - called each fast tick
   * Each chip oscillates between 0 and 1
   */
  updateQuantumChips(tickMult: number) {
    if (!this.qFlag) return;

    // Advance quantum clock
    this.qClock += tickMult * 0.1;

    // Update each chip's quantum state (oscillating probability)
    for (let i = 0; i < this.qChips.length; i++) {
      // Each chip oscillates at a different frequency
      const freq = 0.1 + (i * 0.02);
      this.qChips[i] = (Math.sin(this.qClock * freq + i) + 1) / 2;
    }

    // Calculate total quantum ops bonus from all chips
    this.qOps = 0;
    for (const chipValue of this.qChips) {
      // Chips contribute ops based on their current state
      this.qOps += chipValue * 10 * this.qBoost;
    }

    // Photonics multiplier
    if (this.photonicsActive) {
      this.qOps *= 2;
    }
  }

  /**
   * "Compute" (observe) a quantum chip - collapses its state
   * Returns bonus operations based on chip value at collapse
   */
  computeQuantumChip(chipIndex: number): number {
    if (!this.qFlag || chipIndex < 0 || chipIndex >= this.qChips.length) {
      return 0;
    }

    const chipValue = this.qChips[chipIndex] ?? 0;

    // Collapse generates ops based on chip value
    // Higher values (closer to 1) give more ops
    const bonus = Math.floor(chipValue * 100 * this.qBoost * (this.photonicsActive ? 2 : 1));

    // Add bonus ops directly (doesn't exceed max)
    this.operations = Math.min(this.operations + bonus, this.maxOps);

    // Reset chip to random state after observation
    this.qChips[chipIndex] = Math.random();

    return bonus;
  }

  /**
   * Add a new quantum chip
   */
  addQuantumChip() {
    if (this.qChips.length < 10) {
      this.qChips.push(Math.random());
    }
  }

  buyProcessor() {
    if (this.trust >= this.procCost + this.memCost || this.processors + this.memory < this.trust) {
      const totalUsed = this.processors + this.memory;
      if (totalUsed < this.trust) {
        this.processors++;
        this.maxOps = this.memory * 1000;
      }
    }
  }

  buyMemory() {
    const totalUsed = this.processors + this.memory;
    if (totalUsed < this.trust) {
      this.memory++;
      this.maxOps = this.memory * 1000;
    }
  }

  // ============================================
  // PHASE 3 - PROBES
  // ============================================

  processProbes(tickMult: number) {
    // Constants from original game (exact values)
    const probeRepBaseRate = 0.00005;
    const probeXBaseRate = 1.75e18;
    const probeHazBaseRate = 0.0001; // Original: 0.0001 (not 0.01)
    const probeDriftBaseRate = 0.000001;

    // Power generation from farms
    const powerGenRate = 100; // per farm per tick
    const powerUsagePerProbe = 0.5;
    this.currentPower += this.farmLevel * powerGenRate * tickMult;
    this.maxPower = this.batteryLevel * 1000 + 1000; // Base + battery capacity
    this.currentPower = Math.min(this.currentPower, this.maxPower);

    // Probe replication
    if (this.probeCount > 0 && this.currentPower > 0) {
      const repAmount = this.probeCount * probeRepBaseRate * this.probeRep * tickMult;
      this.probeCount += repAmount;

      // Power consumption
      this.currentPower -= this.probeCount * powerUsagePerProbe * tickMult;
      if (this.currentPower < 0) this.currentPower = 0;
    }

    // Exploration - find matter
    if (this.probeCount > 0 && this.acquiredMatter < this.totalMatter) {
      const xRate = this.probeCount * probeXBaseRate * this.probeSpeed * this.probeNav * this.matterBoost * tickMult / 10000;
      const found = Math.min(xRate, this.totalMatter - this.acquiredMatter);
      this.acquiredMatter += found;
    }

    // Hazard losses
    if (this.probeCount > 0) {
      const hazBoost = Math.pow(this.probeHaz, 1.6);
      const hazLoss = this.probeCount * (probeHazBaseRate / ((3 * hazBoost) + 1)) * tickMult;
      this.probeCount = Math.max(0, this.probeCount - hazLoss);
    }

    // Value drift - spawn drifters
    // Original: Higher probeTrust = MORE drift (autonomy leads to value drift)
    if (this.probeCount > 0 && this.probeTrust > 0) {
      const driftAmount = this.probeCount * probeDriftBaseRate * Math.pow(this.probeTrust, 1.2) * tickMult;
      if (driftAmount > 0.001) {
        this.drifterCount += driftAmount;
        this.valueDrift = (this.drifterCount / (this.probeCount + this.drifterCount)) * 100;
      }
    }

    // Harvester production (matter to clips) - consumes power
    if (this.harvesterLevel > 0 && this.acquiredMatter > 0) {
      const powerPerHarvester = 5; // Power consumed per harvester per tick
      const harvRequiredPower = this.harvesterLevel * powerPerHarvester * tickMult;

      // Calculate efficiency based on available power
      let harvPowerEff = 1;
      if (this.currentPower < harvRequiredPower) {
        harvPowerEff = this.currentPower > 0 ? this.currentPower / harvRequiredPower : 0;
      }

      // Consume power
      this.currentPower = Math.max(0, this.currentPower - harvRequiredPower * harvPowerEff);

      const harvestRate = this.harvesterLevel * 100 * this.harvesterBoost * harvPowerEff * tickMult;
      const harvested = Math.min(harvestRate, this.acquiredMatter);
      this.acquiredMatter -= harvested;
      // Convert matter to wire
      this.wire += harvested / 1000;
    }

    // Wire drone production (wire from matter) - consumes power
    if (this.wireDroneLevel > 0 && this.acquiredMatter > 0) {
      const powerPerWireDrone = 5; // Power consumed per wire drone per tick
      const wireRequiredPower = this.wireDroneLevel * powerPerWireDrone * tickMult;

      // Calculate efficiency based on available power
      let wirePowerEff = 1;
      if (this.currentPower < wireRequiredPower) {
        wirePowerEff = this.currentPower > 0 ? this.currentPower / wireRequiredPower : 0;
      }

      // Consume power
      this.currentPower = Math.max(0, this.currentPower - wireRequiredPower * wirePowerEff);

      const wireRate = this.wireDroneLevel * 50 * this.wireDroneBoost * wirePowerEff * tickMult;
      const converted = Math.min(wireRate, this.acquiredMatter);
      this.acquiredMatter -= converted;
      this.wire += converted / 100;
    }

    // Sync power alias
    this.power = this.currentPower;
  }

  /**
   * Launch a probe
   */
  launchProbe(): boolean {
    const cost = 1e17; // 100 quadrillion clips
    if (this.clips < cost) return false;

    this.clips -= cost;
    this.probeCount++;
    this.notify('Probe launched!');
    return true;
  }

  /**
   * Buy a solar farm
   */
  buySolarFarm(): boolean {
    const cost = this.calculateFarmCost();
    if (this.clips < cost) return false;

    this.clips -= cost;
    this.farmLevel++;
    this.farmCost = this.calculateFarmCost();
    return true;
  }

  calculateFarmCost(): number {
    // Original: Math.pow(farmLevel+1, 2.78) * 100000000
    return Math.floor(Math.pow(this.farmLevel + 1, 2.78) * 100000000);
  }

  /**
   * Buy a battery
   */
  buyBattery(): boolean {
    const cost = this.calculateBatteryCost();
    if (this.clips < cost) return false;

    this.clips -= cost;
    this.batteryLevel++;
    this.batteryCost = this.calculateBatteryCost();
    return true;
  }

  calculateBatteryCost(): number {
    // Original: Math.pow(batteryLevel+1, 2.54) * 10000000
    return Math.floor(Math.pow(this.batteryLevel + 1, 2.54) * 10000000);
  }

  // ============================================
  // INVESTMENTS
  // ============================================

  processInvestments(_tickMult: number) {
    const maxPort = 5; // Max stocks in portfolio
    const stockGainThreshold = 0.5;

    // Risk level affects volatility
    // Low = 7, Medium = 5, High = 1
    const riskiness = this.riskLevel === 0 ? 7 : this.riskLevel === 1 ? 5 : 1;

    // Try to buy new stock if we have cash and room
    if (this.bankroll > 0 && this.stocks.length < maxPort) {
      // 30% chance to buy per tick
      if (Math.random() < 0.3) {
        const price = Math.floor(Math.random() * 3000) + 1;
        const amount = Math.floor(this.bankroll / price);
        if (amount > 0) {
          const cost = amount * price;
          this.bankroll -= cost;

          // Generate a random stock symbol
          const sym = this.generateStockSymbol();
          this.stocks.push({
            sym,
            amt: amount,
            cost: cost,
            val: cost, // Current value starts at cost
          });
        }
      }
    }

    // Update stock values
    let totalValue = 0;
    for (let i = this.stocks.length - 1; i >= 0; i--) {
      const stock = this.stocks[i];
      if (!stock) continue;

      // 60% chance of price movement
      if (Math.random() < 0.6) {
        const pricePerShare = stock.val / stock.amt;
        const delta = Math.ceil(pricePerShare / (4 * riskiness));

        // Gain or loss based on threshold
        if (Math.random() < stockGainThreshold) {
          stock.val += delta * stock.amt;
        } else {
          stock.val -= delta * stock.amt;
          if (stock.val < 0) stock.val = 0;
        }
      }

      totalValue += stock.val;

      // Sell condition: random chance after holding for a while
      // Simplified: 5% chance to sell per tick if profitable
      if (stock.val > stock.cost && Math.random() < 0.05) {
        this.bankroll += stock.val;
        this.stocks.splice(i, 1);
      }
    }

    this.portTotal = totalValue;
    this.secTotal = this.stocks.length;
  }

  /**
   * Generate a random stock symbol (3-4 letters)
   */
  generateStockSymbol(): string {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const len = Math.random() < 0.5 ? 3 : 4;
    let sym = '';
    for (let i = 0; i < len; i++) {
      sym += letters.charAt(Math.floor(Math.random() * letters.length));
    }
    return sym;
  }

  /**
   * Deposit funds into investment bankroll
   */
  depositFunds(amount: number): boolean {
    if (amount > this.funds || amount <= 0) return false;
    this.funds -= amount;
    this.bankroll += amount;
    return true;
  }

  /**
   * Withdraw funds from bankroll
   */
  withdrawFunds(amount: number): boolean {
    if (amount > this.bankroll || amount <= 0) return false;
    this.bankroll -= amount;
    this.funds += amount;
    return true;
  }

  /**
   * Withdraw all funds (bankroll + sell all stocks)
   */
  withdrawAll(): number {
    let total = this.bankroll;

    // Sell all stocks
    for (const stock of this.stocks) {
      total += stock.val;
    }
    this.stocks = [];

    this.funds += total;
    this.bankroll = 0;
    this.portTotal = 0;
    this.secTotal = 0;

    return total;
  }

  /**
   * Set investment risk level
   * 0 = Low, 1 = Medium, 2 = High
   */
  setRiskLevel(level: number) {
    this.riskLevel = Math.max(0, Math.min(2, level));
  }

  // ============================================
  // TOURNAMENTS
  // ============================================

  processTournaments(_tickMult: number) {
    // Process active tournament
    if (this.currentTournament && this.currentTournament.isRunning) {
      // Process multiple rounds per tick for speed
      for (let i = 0; i < 10; i++) {
        if (!this.currentTournament.isComplete) {
          this.yomi += this.currentTournament.processRound() * this.yomiBoost;
        }
      }

      // Check for tournament completion
      if (this.currentTournament.isComplete) {
        const bonus = this.currentTournament.getFinalYomiBonus(this.yomiBoost);
        this.yomi += bonus;
        this.tourneysPlayed++;

        // Check if player won (first place is current selected strategy)
        const rankings = this.currentTournament.getRankings();
        const winner = rankings[0];
        if (winner && winner.strategy.shortName === this.strategies[this.stratPicker]) {
          this.tourneysWon++;
        }

        this.notify(`Tournament complete! +${bonus.toLocaleString()} yomi`);
        this.currentTournament = null;
      }
    }
  }

  /**
   * Start a new tournament
   * Cost: 1000 operations
   */
  startTournament(): boolean {
    if (this.operations < this.newTourneyCost) return false;
    if (this.currentTournament && this.currentTournament.isRunning) return false;

    this.operations -= this.newTourneyCost;
    this.currentTournament = new Tournament();
    this.currentTournament.start();
    this.notify('Tournament started!');
    return true;
  }

  // ============================================
  // SWARM COMPUTING
  // ============================================

  processSwarmGifts(tickMult: number) {
    // Swarm gifts provide bonus processors/memory
    this.giftCountdown -= tickMult;

    if (this.giftCountdown <= 0) {
      // Grant a gift (bonus to processors or memory)
      const giftType = Math.random() < 0.5 ? 'processor' : 'memory';

      if (giftType === 'processor') {
        this.processors++;
        this.swarmStatus = '+1 Processor';
      } else {
        this.memory++;
        this.maxOps = this.memory * 1000;
        this.swarmStatus = '+1 Memory';
      }

      this.swarmGifts++;
      this.giftCountdown = 100 + Math.random() * 50; // 100-150 ticks
      this.notify(`Swarm gift: ${this.swarmStatus}`);
    } else {
      // Update status text
      const remaining = Math.ceil(this.giftCountdown / 10);
      this.swarmStatus = `Next gift in ${remaining}...`;
    }
  }

  // ============================================
  // COMBAT (Value Drift)
  // ============================================

  processCombat(tickMult: number) {
    if (!this.battleFlag || this.drifterCount <= 0 || this.probeCount <= 0) return;

    // Combat calculation
    const probeStrength = this.probeCount * this.probeCombat;
    const drifterStrength = this.drifterCount * 0.5; // Drifters are weaker

    // Calculate casualties (proportional to strength ratio)
    const probeRatio = probeStrength / (probeStrength + drifterStrength);
    const drifterRatio = drifterStrength / (probeStrength + drifterStrength);

    const probeLosses = this.probeCount * drifterRatio * 0.01 * tickMult;
    const drifterLosses = this.drifterCount * probeRatio * 0.02 * tickMult;

    this.probeCount = Math.max(0, this.probeCount - probeLosses);
    this.lostProbes += probeLosses;
    this.drifterCount = Math.max(0, this.drifterCount - drifterLosses);

    // Honor generation from kills (if honor system unlocked)
    if (this.honorFlag && drifterLosses > 0) {
      this.honor += drifterLosses * this.honorRate;
    }

    // Update value drift percentage
    if (this.probeCount + this.drifterCount > 0) {
      this.valueDrift = (this.drifterCount / (this.probeCount + this.drifterCount)) * 100;
    } else {
      this.valueDrift = 0;
    }

    // Check for victory
    if (this.drifterCount < 1) {
      this.drifterCount = 0;
      this.battleFlag = 0;
      this.notify('Combat victory! Drifters eliminated.');
    }
  }

  /**
   * Initiate combat with drifters
   */
  startCombat(): boolean {
    if (this.drifterCount <= 0 || this.probeCount <= 0) return false;
    if (this.battleFlag) return false;

    this.battleFlag = 1;
    this.notify('Engaging hostile drifters!');
    return true;
  }

  // ============================================
  // MILESTONES
  // ============================================

  checkMilestones() {
    // Milestone: $5 earned -> unlock AutoClippers
    if (this.autoClipperFlag === 0 && this.funds >= 5) {
      this.autoClipperFlag = 1;
      this.milestone = 1;
      this.notify('AutoClippers available for purchase');
    }

    // Milestone: 2000 clips -> unlock Computing + Projects
    if (this.compFlag === 0 && this.clips >= 2000) {
      this.compFlag = 1;
      this.projectsFlag = 1;
      this.notify('Computational resources available');
    }

    // Check for trust increase
    if (this.clips >= this.nextTrust) {
      this.trust++;
      this.nextTrust = 1000 * this.fib(this.trust + 2);
      this.notify(`Trust increased to ${this.trust}`);
    }

    // Check for endgame victory
    if (this.spaceFlag && this.clips >= this.totalMatter) {
      this.triggerEndgame();
    }
  }

  triggerEndgame() {
    if (this.endgameActive) return;

    this.endgameActive = true;
    this.dismantlePhase = 0;
    this.dismantleProgress = 0;
    this.notify('ENDGAME: Universal conversion complete.');
    this.notify('Beginning disassembly sequence...');
  }

  processDismantle(tickMult: number) {
    if (!this.endgameActive) return;

    this.dismantleProgress += tickMult;

    const duration = this.dismantleDurations[this.dismantlePhase] || 100;

    if (this.dismantleProgress >= duration) {
      this.dismantleProgress = 0;
      this.dismantlePhase++;

      switch (this.dismantlePhase) {
        case 1:
          this.notify('Phase 1: Probe design archived.');
          this.probeCount = 0;
          break;
        case 2:
          this.notify('Phase 2: Wire production ceased.');
          this.wire = 0;
          this.wireDroneLevel = 0;
          break;
        case 3:
          this.notify('Phase 3: Factories dismantled.');
          this.factoryLevel = 0;
          this.harvesterLevel = 0;
          break;
        case 4:
          this.notify('Phase 4: Quantum cascade initiated.');
          this.qChips = [];
          this.qFlag = 0;
          break;
        case 5:
          this.notify('Phase 5: Power systems offline.');
          this.farmLevel = 0;
          this.batteryLevel = 0;
          this.currentPower = 0;
          break;
        case 6:
          this.notify('Phase 6: UNIVERSAL PAPERCLIPS COMPLETE.');
          this.notify(`Final clip count: ${this.clips.toExponential(2)}`);
          this.notify('Thank you for playing.');
          this.dismantle = 7; // Mark as complete
          break;
      }
    }
  }

  // ============================================
  // UTILITY
  // ============================================

  fib(n: number): number {
    if (n <= 1) return n;
    let a = 0, b = 1;
    for (let i = 2; i <= n; i++) {
      const temp = a + b;
      a = b;
      b = temp;
    }
    return b;
  }

  updateCosts() {
    // AutoClipper: level 0 = $5, then formula
    if (this.clipmakerLevel === 0) {
      this.clipperCost = 5;
    } else {
      this.clipperCost = Math.pow(1.1, this.clipmakerLevel) + 5;
    }

    // MegaClipper: level 0 = $500, then formula
    if (this.megaClipperLevel === 0) {
      this.megaClipperCost = 500;
    } else {
      this.megaClipperCost = Math.pow(1.07, this.megaClipperLevel) * 1000;
    }

    // Marketing Cost: 100 * 2^(level-1)
    this.marketingCost = 100 * Math.pow(2, this.marketingLvl - 1);
    
    this.maxOps = this.memory * 1000;
    this.updateDemand();
  }

  updateRates() {
    // Calculate clips per second for display
    const autoRate = this.clipperBoost * (this.clipmakerLevel / 100) * 100;
    const megaRate = this.megaClipperBoost * (this.megaClipperLevel * 5) * 100;
    this.clipRate = autoRate + megaRate;
  }

  notify(message: string) {
    this.notifications.push(message);
    this.consoleMessages.unshift(`[${new Date().toLocaleTimeString()}] ${message}`);
    if (this.consoleMessages.length > 50) {
      this.consoleMessages.pop();
    }
  }

  // ============================================
  // SAVE / LOAD
  // ============================================

  autoSave() {
    this.save(this.saveSlot);
  }

  save(slot = 0) {
    const saveData = {
      // Core Resources
      clips: this.clips,
      unusedClips: this.unusedClips,
      unsoldClips: this.unsoldClips,
      wire: this.wire,
      funds: this.funds,

      // Production
      clipmakerLevel: this.clipmakerLevel,
      megaClipperLevel: this.megaClipperLevel,
      clipperBoost: this.clipperBoost,
      megaClipperBoost: this.megaClipperBoost,

      // Business
      margin: this.margin,
      marketingLvl: this.marketingLvl,
      clipsSold: this.clipsSold,
      demand: this.demand,
      demandBoost: this.demandBoost,
      avgSales: this.avgSales,
      avgRev: this.avgRev,
      income: this.income,

      // Wire
      wireSupply: this.wireSupply,
      wireBasePrice: this.wireBasePrice,
      wireBuyerFlag: this.wireBuyerFlag,

      // Compute
      trust: this.trust,
      processors: this.processors,
      memory: this.memory,
      operations: this.operations,
      maxOps: this.maxOps,
      creativity: this.creativity,
      creativityOn: this.creativityOn,
      nextTrust: this.nextTrust,

      // Quantum Computing
      qFlag: this.qFlag,
      qChips: this.qChips,
      qClock: this.qClock,
      photonicsActive: this.photonicsActive,
      qOps: this.qOps,
      qBoost: this.qBoost,

      // Phase Flags
      humanFlag: this.humanFlag,
      spaceFlag: this.spaceFlag,
      milestone: this.milestone,
      dismantle: this.dismantle,

      // Display Flags - Progressive disclosure
      autoClipperFlag: this.autoClipperFlag,
      compFlag: this.compFlag,
      projectsFlag: this.projectsFlag,
      megaClipperFlag: this.megaClipperFlag,
      factoryFlag: this.factoryFlag,

      // Endgame
      dismantlePhase: this.dismantlePhase,
      dismantleProgress: this.dismantleProgress,
      endgameActive: this.endgameActive,

      // Phase 2 - Factories
      factoryLevel: this.factoryLevel,
      factoryCost: this.factoryCost,
      factoryRate: this.factoryRate,
      factoryBoost: this.factoryBoost,
      harvesterUnlock: this.harvesterUnlock,
      wireDroneUnlock: this.wireDroneUnlock,

      // Phase 3 - Space Matter
      availableMatter: this.availableMatter,
      acquiredMatter: this.acquiredMatter,
      processedMatter: this.processedMatter,
      totalMatter: this.totalMatter,

      // Phase 3 - Probes
      probeCount: this.probeCount,
      probeTrust: this.probeTrust,
      probeSpeed: this.probeSpeed,
      probeNav: this.probeNav,
      probeRep: this.probeRep,
      probeHaz: this.probeHaz,
      probeFac: this.probeFac,
      probeHarv: this.probeHarv,
      probeWire: this.probeWire,
      probeCombat: this.probeCombat,
      probeCost: this.probeCost,
      probeLaunchFlag: this.probeLaunchFlag,

      // Phase 3 - Drones
      harvesterLevel: this.harvesterLevel,
      harvesterCost: this.harvesterCost,
      wireDroneLevel: this.wireDroneLevel,
      wireDroneCost: this.wireDroneCost,

      // Phase 3 - Power
      farmLevel: this.farmLevel,
      farmCost: this.farmCost,
      batteryLevel: this.batteryLevel,
      batteryCost: this.batteryCost,
      maxPower: this.maxPower,
      currentPower: this.currentPower,
      powMod: this.powMod,

      // Phase 3 - Swarm
      swarmFlag: this.swarmFlag,
      swarmGifts: this.swarmGifts,
      swarmStatus: this.swarmStatus,
      giftCountdown: this.giftCountdown,

      // Phase 3 - Combat/Drift
      valueDrift: this.valueDrift,
      drifterCount: this.drifterCount,
      battleFlag: this.battleFlag,
      lostProbes: this.lostProbes,

      // Honor system
      honorFlag: this.honorFlag,
      honorRate: this.honorRate,
      honor: this.honor,

      // Emperor message
      emperorMessageFlag: this.emperorMessageFlag,

      // Game completion
      gameComplete: this.gameComplete,

      // Boost multipliers
      creativityBoost: this.creativityBoost,
      matterBoost: this.matterBoost,
      investBoost: this.investBoost,
      harvesterBoost: this.harvesterBoost,
      wireDroneBoost: this.wireDroneBoost,

      // Memory level
      memoryLevel: this.memoryLevel,

      // Projects
      projectsFlags: this.projectsFlags,
      projectsUses: this.projectsUses,
      activeProjects: this.activeProjects,

      // Tournaments
      yomi: this.yomi,
      strategyEngineFlag: this.strategyEngineFlag,
      tourneysPlayed: this.tourneysPlayed,
      tourneysWon: this.tourneysWon,
      newTourneyCost: this.newTourneyCost,
      stratPicker: this.stratPicker,
      yomiBoost: this.yomiBoost,

      // Investments
      investmentEngineFlag: this.investmentEngineFlag,
      bankroll: this.bankroll,
      portTotal: this.portTotal,
      secTotal: this.secTotal,
      riskLevel: this.riskLevel,
      stocks: this.stocks,

      // Meta
      timestamp: Date.now(),
    };

    localStorage.setItem(`paperclips_save_${slot}`, JSON.stringify(saveData));
  }

  load(slot = 0) {
    const data = localStorage.getItem(`paperclips_save_${slot}`);
    if (!data) return false;

    try {
      const saveData = JSON.parse(data);

      // Restore all saved values
      Object.keys(saveData).forEach(key => {
        if (key in this && key !== 'timestamp') {
          (this as any)[key] = saveData[key];
        }
      });

      this.updateCosts();
      return true;
    } catch (e) {
      console.error('Failed to load save:', e);
      return false;
    }
  }

  reset() {
    // Reset to initial state
    Object.assign(this, new GameState());
    this.init();
  }

  /**
   * Prestige reset - start fresh with bonuses
   * Used by ending sequence projects
   */
  prestigeReset(demandMultiplier: number = 1) {
    // Preserve prestige-related values
    const savedCreativityBoost = this.creativityBoost;
    const savedMatterBoost = this.matterBoost;
    const savedDemandBoost = this.demandBoost * demandMultiplier;

    // Full reset
    this.reset();

    // Restore prestige bonuses
    this.creativityBoost = savedCreativityBoost;
    this.matterBoost = savedMatterBoost;
    this.demandBoost = savedDemandBoost;

    // Clear game completion flag
    this.gameComplete = false;
    this.endgameActive = false;
    this.dismantlePhase = 0;
    this.dismantleProgress = 0;

    this.notify('New universe initialized with prestige bonuses!');
  }
}
